import React, { useState, useEffect } from "react";
import { useGlobalContext } from "../../context/stateContext";
import { useAuth } from "../../context/Auth";
import { useParams, Link } from "react-router-dom";
import { toast } from "react-toastify";
import { RotatingLines } from "react-loader-spinner";
import Swal from "sweetalert2";
import Loading from "../../components/Loading/Loading";
import {
  Building2,
  Pill,
  Save,
  Scale,
  ArrowLeft,
  LayoutGrid,
  History,
  Info
} from "lucide-react";

const BranchDrugReconciliation = () => {
  const { inventory_id } = useParams();
  const { requestWithAuth } = useAuth();
  const { activeSession } = useGlobalContext();
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [newStock, setNewStock] = useState("");
  const [drugDetails, setDrugDetails] = useState(null);

  const fetchDrugDetails = async () => {
    setLoading(true);
    try {
      const data = await requestWithAuth(
        `/inventory/branch/${inventory_id}/item/`,
        "GET"
      );
      setDrugDetails(data);
    } catch (error) {
      toast.error("Failed to get inventory detail");
    } finally {
      setLoading(false);
    }
  };

  const updateDrugStock = async (e) => {
    e.preventDefault();
    if (!newStock || parseFloat(newStock) < 0) {
      return toast.error("Please enter a valid stock quantity");
    }

    setUpdating(true);
    try {
      await requestWithAuth(
        `/inventory/branch/${inventory_id}/item/`,
        "PUT",
        {
          quantity: newStock,
        },
        {
          "Content-Type": "application/json"
        }
      );
      Swal.fire({
        icon: "success",
        title: "Success",
        text: "Drug reconciled successfully",
      }).then(() => {
        window.location.href = document.referrer;
      });
    } catch (error) {
      toast.error("Failed to update stock levels");
    } finally {
      setUpdating(false);
    }
  };

  useEffect(() => {
    fetchDrugDetails();
  }, []);

  if (loading) return <Loading />;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header and Breadcrumbs */}
        <div className="mb-8">
          <div className="flex items-center gap-2 text-gray-500 text-sm mb-2">
            <Link to="/inventory" className="hover:text-blue-600 transition-colors">Inventory</Link>
            <span>/</span>
            <span className="text-gray-900 font-medium">Reconciliation</span>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => window.history.back()}
                className="p-2 hover:bg-white rounded-lg border border-transparent hover:border-gray-200 transition-all text-gray-600"
              >
                <ArrowLeft size={20} />
              </button>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                <Scale className="text-blue-600" size={28} />
                Stock Reconciliation
              </h1>
            </div>

            <div className="bg-blue-50 border border-blue-100 rounded-lg px-4 py-2 flex items-center gap-3">
              <Building2 size={18} className="text-blue-600" />
              <div>
                <p className="text-[10px] uppercase tracking-wider text-blue-600 font-bold">Current Branch</p>
                <p className="text-sm font-semibold text-gray-900">{activeSession?.branch?.name}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          {/* Card Header Section */}
          <div className="p-8 border-b border-gray-100 bg-gradient-to-r from-blue-50/50 to-indigo-50/50">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-white rounded-xl shadow-sm border border-blue-100 text-blue-600">
                <Pill size={24} />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  {drugDetails?.drug?.name}
                </h2>
                <div className="flex items-center gap-4 mt-2">
                  <span className="flex items-center gap-1.5 text-sm text-gray-500">
                    <History size={14} />
                    Current Qty: <span className="font-semibold text-gray-900">{drugDetails?.quantity}</span>
                  </span>
                  <span className="h-4 w-px bg-gray-200" />
                  <span className="flex items-center gap-1.5 text-sm text-gray-500">
                    <LayoutGrid size={14} />
                    Drug ID: <span className="font-semibold text-gray-900">#{drugDetails?.drug?.id}</span>
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Form Section */}
          <div className="p-8">
            <form onSubmit={updateDrugStock} className="space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Read-only details */}
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Drug Name</label>
                    <div className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-gray-600 text-sm font-medium">
                      {drugDetails?.drug?.name}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Location / Branch</label>
                    <div className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-gray-600 text-sm font-medium">
                      {drugDetails?.branch?.name}
                    </div>
                  </div>
                </div>

                {/* Input section */}
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Current System Stock</label>
                    <div className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-gray-600 text-sm font-medium flex items-center justify-between">
                      <span>{drugDetails?.quantity}</span>
                      <span className="text-xs text-gray-400">Read-only</span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">
                      New Physical Stock <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      required
                      placeholder="Enter actual physical count"
                      onChange={(e) => setNewStock(e.target.value)}
                      className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all font-medium"
                    />
                  </div>
                </div>
              </div>

              {/* Alert Note */}
              <div className="bg-amber-50 rounded-xl p-4 border border-amber-100 flex gap-3">
                <Info className="text-amber-600 flex-shrink-0" size={20} />
                <p className="text-sm text-amber-800">
                  <span className="font-bold">Important:</span> This action will directly update the stock quantity of this drug in the system. Please ensure your physical count is accurate before submitting. Please update the batches after reconciliation.
                </p>
              </div>

              {/* Action Buttons */}
              <div className="pt-4 flex items-center justify-end gap-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => window.history.back()}
                  className="px-6 py-2.5 text-sm font-bold text-gray-500 hover:text-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  disabled={updating}
                  className={`flex items-center gap-2 px-8 py-2.5 rounded-xl text-sm font-bold shadow-sm transition-all
                    ${updating
                      ? "bg-blue-100 text-blue-400 cursor-not-allowed"
                      : "bg-blue-600 text-white hover:bg-blue-700 hover:shadow-md active:scale-[0.98]"
                    }`}
                >
                  {updating ? (
                    <>
                      <RotatingLines
                        visible={true}
                        height="18"
                        width="18"
                        strokeColor="currentColor"
                        strokeWidth="5"
                        animationDuration="0.75"
                        ariaLabel="rotating-lines-loading"
                      />
                      Updating...
                    </>
                  ) : (
                    <>
                      <Save size={18} />
                      Save Reconciliation
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Footer Info */}
        {/* <p className="text-center mt-8 text-sm text-gray-400">
          Last updated by {activeSession?.user?.first_name} {activeSession?.user?.last_name}
        </p> */}
      </div>
    </div>
  );
};

export default BranchDrugReconciliation;

